/**
 * Custom hook for fetching and managing module revenue data via GraphQL
 */

import { useState, useCallback, useEffect } from "react";
import { message } from "antd";
import { useAuth0 } from "../../../react-auth0-spa";
import { useAppContext } from "../../../context";
import config from "../../../api_config.json";

// GraphQL query to fetch all bi_module_revenue records
const GET_MODULE_REVENUE_QUERY = `
  query GetModuleRevenue($first: Int!, $order: [Bi_module_revenueOrder!]) {
    bi_module_revenues(
      first: $first,
      order: $order
    ) {
      count
      edges {
        node {
          objectId
          module_name
          module_sku
          module_pointer {
            objectId
          }
          total_mrr
          total_arr
          active_subscriptions_count
          monthly_subscribers_count
          yearly_subscribers_count
          average_price
          total_quantity
          is_product_line
          createdAt
          updatedAt
        }
      }
    }
  }
`;

const useModuleRevenueData = () => {
  const { getTokenSilently } = useAuth0();
  const { isProd } = useAppContext();

  const [state, setState] = useState({
    loading: true,
    recalculating: false,
    data: [],
    lastCalculatedAt: null,
    error: null,
  });

  const getGraphQLUrl = useCallback(() => {
    const env = isProd ? "prod" : "dev";
    return `${config.baseUrl[env]}/graphql?raw=true`;
  }, [isProd]);

  const getBaseUrl = useCallback(() => {
    const env = isProd ? "prod" : "dev";
    return config.baseUrl[env];
  }, [isProd]);

  /**
   * Fetch module revenue data from GraphQL
   */
  const fetchData = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const token = await getTokenSilently();
      const response = await fetch(getGraphQLUrl(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          query: GET_MODULE_REVENUE_QUERY,
          variables: {
            first: 1000, // Fetch all modules
            order: ["total_mrr_DESC"],
          },
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.errors) {
        throw new Error(result.errors[0]?.message || "GraphQL error");
      }

      // Handle both wrapped and unwrapped GraphQL responses
      const biModuleRevenues = result.data?.bi_module_revenues || result.bi_module_revenues;
      const records = biModuleRevenues?.edges?.map(
        (edge) => edge.node
      ) || [];

      // Get the most recent updatedAt as lastCalculatedAt
      const lastCalculatedAt = records.length > 0
        ? records.reduce((latest, record) => {
            const recordDate = new Date(record.updatedAt);
            return recordDate > latest ? recordDate : latest;
          }, new Date(0))
        : null;

      setState((prev) => ({
        ...prev,
        loading: false,
        data: records,
        lastCalculatedAt: lastCalculatedAt?.toISOString() || null,
        error: null,
      }));
    } catch (error) {
      console.error("Error fetching module revenue data:", error);
      message.error("Errore nel caricamento dei dati. Riprova più tardi.");
      setState((prev) => ({
        ...prev,
        loading: false,
        error: error.message,
      }));
    }
  }, [getTokenSilently, getGraphQLUrl]);

  /**
   * Trigger recalculation of module revenue data
   */
  const recalculate = useCallback(async () => {
    setState((prev) => ({ ...prev, recalculating: true }));

    try {
      const token = await getTokenSilently();
      const response = await fetch(`${getBaseUrl()}/bi/calculate/module-revenue`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      message.success("Ricalcolo completato con successo!");

      // Refetch data after recalculation
      await fetchData();
    } catch (error) {
      console.error("Error recalculating module revenue:", error);
      message.error("Errore durante il ricalcolo. Riprova più tardi.");
    } finally {
      setState((prev) => ({ ...prev, recalculating: false }));
    }
  }, [getTokenSilently, getBaseUrl, fetchData]);

  /**
   * Refresh data (alias for fetchData)
   */
  const refresh = useCallback(() => {
    return fetchData();
  }, [fetchData]);

  // Fetch data on mount
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    ...state,
    fetchData,
    recalculate,
    refresh,
  };
};

export default useModuleRevenueData;
